import { useMemo } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';

// Feature toggle: when true, a spot (ui_click) AI script/knowledge entry is
// only creatable/visible/selectable when the spot is owned by a scriptable UE
// trigger region (RegionIndex.spotToRegion) — i.e. "linked to a trigger", as
// opposed to a standalone media placement with no trigger volume behind it in
// the build. Flip to false to restore the old behavior where any spot can
// carry its own standalone ui_click script/knowledge. Shared by
// AiScriptsTreeSection.tsx (sidebar/create-flow) and EditorMode.tsx (WS-driven
// live-selection bridges), so both stay in sync.
export const REQUIRE_SPOT_TRIGGER_LINK = true;

export interface RegionIndex {
  // Scriptable region names for the current map (type MytaAITrigger or
  // MytaVideoTrigger — see toRegionList below).
  regionNames: string[];
  // regionName -> the spot ueIdentifiers it owns (region.spots[]).
  regionSpots: Record<string, string[]>;
  // spotId (lowercased) -> owning region name. Lets a spot resolve to its
  // region so a script created for the region also covers the spot.
  spotToRegion: Record<string, string>;
  // Name of the region flagged isStartingPoint (the map's real "on join"
  // trigger), when the current map has one — undefined for maps that still
  // rely on the synthetic 'landing' pseudo-trigger.
  startingPointRegionName: string | undefined;
}

const EMPTY_INDEX: RegionIndex = {
  regionNames: [],
  regionSpots: {},
  spotToRegion: {},
  startingPointRegionName: undefined,
};

// Both known UE region types are scriptable (admin shows regions of either
// type, splitting them into Triggers/Areas tabs by this same value).
const SCRIPTABLE_REGION_TYPES = new Set(['MytaAITrigger', 'MytaVideoTrigger']);

/**
 * Reads the scriptable regions — and, crucially, the `spots[]` array each
 * region owns — from the event's pixel-streaming client resources
 * (`clientParams.resources.maps[].regions`, the same source the admin uses),
 * falling back to the room's map metadata. This is the bridge between an AI
 * trigger ("Expo #1") and the spots that live inside it ("WallArt_Pic_01"), so
 * one script can be created for both at once.
 */
export const useRegionIndex = (mapName: string | undefined): RegionIndex => {
  const { currentRoom, pixelStreamingClient } = useMytaverseEvent();

  return useMemo<RegionIndex>(() => {
    type Region = {
      name?: unknown;
      type?: unknown;
      spots?: unknown;
      isStartingPoint?: unknown;
    };
    type ResourceMap = { name?: unknown; regions?: Region[] };

    const toRegionList = (regions: Region[] | undefined): Region[] =>
      Array.isArray(regions)
        ? regions.filter(
            (r) => typeof r?.type === 'string' && SCRIPTABLE_REGION_TYPES.has(r.type),
          )
        : [];

    const resourceMaps = (
      pixelStreamingClient as
        | { clientParams?: { resources?: { maps?: ResourceMap[] } } }
        | null
        | undefined
    )?.clientParams?.resources?.maps;

    let regions: Region[] = [];
    if (Array.isArray(resourceMaps) && resourceMaps.length > 0) {
      // The resource map name is a path like "/…/Maps/Map_SpiralMuseum"; prefer
      // the one matching the current map, otherwise flatten all maps.
      const scoped = mapName
        ? resourceMaps.filter(
            (m) => typeof m?.name === 'string' && m.name.includes(mapName),
          )
        : [];
      const source = scoped.length > 0 ? scoped : resourceMaps;
      regions = source.flatMap((m) => toRegionList(m?.regions));
    }

    if (regions.length === 0) {
      const roomRegions = (
        currentRoom?.map as { regions?: Region[] } | undefined
      )?.regions;
      regions = toRegionList(roomRegions);
    }

    if (regions.length === 0) {
      return EMPTY_INDEX;
    }

    const regionNames: string[] = [];
    const regionSpots: Record<string, string[]> = {};
    const spotToRegion: Record<string, string> = {};
    let startingPointRegionName: string | undefined;

    for (const region of regions) {
      const name = typeof region.name === 'string' ? region.name : '';
      if (!name) {
        continue;
      }
      if (!regionNames.includes(name)) {
        regionNames.push(name);
      }
      if (region.isStartingPoint === true && !startingPointRegionName) {
        startingPointRegionName = name;
      }
      const spots = Array.isArray(region.spots)
        ? region.spots.filter((s): s is string => typeof s === 'string' && !!s)
        : [];
      regionSpots[name] = spots;
      for (const spot of spots) {
        spotToRegion[spot.toLowerCase()] = name;
      }
    }

    return { regionNames, regionSpots, spotToRegion, startingPointRegionName };
  }, [pixelStreamingClient, mapName, currentRoom?.map]);
};
